import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api, { resolveImageUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { MapPin, Navigation, Clock, Users, Search, Loader2, Calendar, Map, Share2, ArrowRight, User } from "lucide-react";
import ReviewBadge from "../components/ReviewBadge";
import MapComponent from "../components/MapComponent";

const CITY_COORDS = {
  "yaounde": { lat: 3.848, lng: 11.5021 }, "douala": { lat: 4.0511, lng: 9.7679 },
  "bafoussam": { lat: 5.4737, lng: 10.4176 }, "bamenda": { lat: 5.9597, lng: 10.1460 },
  "garoua": { lat: 9.3014, lng: 13.3987 }, "maroua": { lat: 10.5910, lng: 14.3158 },
  "ngaoundere": { lat: 7.3215, lng: 13.5844 }, "kribi": { lat: 2.9408, lng: 9.9098 },
  "limbe": { lat: 4.0219, lng: 9.2081 }, "buea": { lat: 4.1597, lng: 9.2295 },
};

function getCityCoords(name) {
  if (!name) return null;
  const key = name.toLowerCase().trim();
  for (const [city, coords] of Object.entries(CITY_COORDS)) {
    if (key.includes(city) || city.includes(key)) return coords;
  }
  return null;
}

function shareRideWhatsApp(ride) {
  const text = `*TrigoTour - Trajet*\n\n${ride.departure} -> ${ride.destination}\nDate: ${ride.departure_date} a ${ride.departure_time}\nPrix: ${ride.price_per_seat?.toLocaleString()} XAF/place\nPlaces: ${ride.available_seats - ride.booked_seats}\n\nReservez: ${window.location.origin}/rides?highlight=${ride.id}`;
  window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
}

export default function RidesPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [departure, setDeparture] = useState(searchParams.get("departure") || "");
  const [destination, setDestination] = useState(searchParams.get("destination") || "");
  const [mapRide, setMapRide] = useState(null);

  const fetchRides = useCallback(async (dep = "", dest = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dep) params.append("departure", dep);
      if (dest) params.append("destination", dest);
      const { data } = await api.get(`/rides?${params.toString()}`);
      setRides(data);
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.error(e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRides(departure, destination); }, [fetchRides, departure, destination]);

  useEffect(() => {
    const hId = searchParams.get("highlight");
    if (hId && rides.length > 0) {
      const el = document.querySelector(`[data-testid="ride-card-${hId}"]`);
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("ring-2", "ring-[#FACC15]"); setTimeout(() => el.classList.remove("ring-2", "ring-[#FACC15]"), 3000); }
    }
  }, [searchParams, rides]);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="rides-page">
      {/* Sticky search */}
      <div className="border-b border-gray-100 bg-white sticky top-[72px] z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="bg-[#F7F7F9] rounded-full p-1.5 flex items-center gap-0">
            <div className="flex-1 flex items-center gap-2 px-4">
              <MapPin className="w-4 h-4 text-[#FACC15] shrink-0" />
              <Input value={departure} onChange={e => setDeparture(e.target.value)} placeholder="Ville de depart" className="border-0 shadow-none h-9 text-sm bg-transparent focus-visible:ring-0 p-0" data-testid="search-departure" />
            </div>
            <div className="w-px h-7 bg-gray-300" />
            <div className="flex-1 flex items-center gap-2 px-4">
              <Navigation className="w-4 h-4 text-[#0A2540] shrink-0" />
              <Input value={destination} onChange={e => setDestination(e.target.value)} placeholder="Destination" className="border-0 shadow-none h-9 text-sm bg-transparent focus-visible:ring-0 p-0" data-testid="search-destination" />
            </div>
            <Button onClick={() => fetchRides(departure, destination)} className="bg-[#FACC15] text-[#0A2540] hover:bg-[#eab308] rounded-full h-9 px-5 font-semibold text-sm shrink-0" data-testid="search-btn">
              <Search className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Chercher</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <p className="text-sm text-gray-500 mb-6">{rides.length} trajet(s) disponible(s)</p>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-gray-300" /></div>
        ) : rides.length === 0 ? (
          <div className="text-center py-24">
            <MapPin className="w-14 h-14 mx-auto text-gray-200 mb-4" />
            <p className="text-gray-500 text-lg" style={{ fontFamily: 'Outfit' }}>Aucun trajet disponible</p>
            <p className="text-sm text-gray-400 mt-2">Modifiez vos criteres de recherche</p>
          </div>
        ) : (
          <div className="space-y-6">
            {rides.map(ride => {
              const seats = ride.available_seats - ride.booked_seats;
              return (
                <div key={ride.id} className="group rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all bg-white" data-testid={`ride-card-${ride.id}`}>
                  <div className="flex flex-col sm:flex-row">
                    {/* Image */}
                    {ride.image_url && (
                      <div className="sm:w-52 sm:min-w-[13rem] aspect-[4/3] sm:aspect-auto overflow-hidden rounded-t-2xl sm:rounded-l-2xl sm:rounded-tr-none shrink-0">
                        <img src={resolveImageUrl(ride.image_url)} alt="Trajet" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                    )}
                    {/* Content */}
                    <div className="flex-1 p-5 sm:p-6 flex flex-col justify-between">
                      <div>
                        {/* Route */}
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-base sm:text-lg font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>{ride.departure}</span>
                          <ArrowRight className="w-4 h-4 text-gray-300" />
                          <span className="text-base sm:text-lg font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>{ride.destination}</span>
                        </div>
                        {/* Meta */}
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 mb-3">
                          <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{ride.departure_date}</span>
                          <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{ride.departure_time}</span>
                          <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{seats} place{seats > 1 ? "s" : ""}</span>
                        </div>
                        {/* Driver */}
                        <div className="flex items-center gap-2">
                          {ride.driver_photo ? (
                            <img src={resolveImageUrl(ride.driver_photo)} alt={ride.driver_name} className="w-8 h-8 rounded-full object-cover border border-gray-200" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-[#F7F7F9] flex items-center justify-center"><User className="w-4 h-4 text-gray-400" /></div>
                          )}
                          <span className="text-sm text-gray-600">{ride.driver_name}</span>
                          {ride.driver_rating > 0 && <ReviewBadge rating={ride.driver_rating} totalReviews={ride.driver_total_reviews} />}
                        </div>
                      </div>
                      {/* Footer */}
                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-50">
                        <p className="text-lg sm:text-xl font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>{ride.price_per_seat?.toLocaleString()} <span className="text-sm font-normal text-gray-500">XAF / place</span></p>
                        <div className="flex items-center gap-2">
                          <button onClick={() => shareRideWhatsApp(ride)} className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center hover:border-[#25D366] hover:text-[#25D366] text-gray-400 transition-colors" data-testid={`share-ride-${ride.id}`}>
                            <Share2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => setMapRide(mapRide?.id === ride.id ? null : ride)} className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center hover:border-[#0A2540] hover:text-[#0A2540] text-gray-400 transition-colors" data-testid={`map-toggle-${ride.id}`}>
                            <Map className="w-4 h-4" />
                          </button>
                          {user ? (
                            <Button onClick={() => { /* handled in passenger dashboard */ }} className="bg-[#FACC15] text-[#0A2540] hover:bg-[#eab308] rounded-full font-semibold text-sm px-5 h-9" data-testid={`book-ride-${ride.id}`}>
                              Reserver
                            </Button>
                          ) : (
                            <a href="/login"><Button className="bg-[#0A2540] text-white hover:bg-[#0f365c] rounded-full font-medium text-sm px-5 h-9">Connexion</Button></a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Map */}
                  {mapRide?.id === ride.id && (
                    <div className="border-t border-gray-100 p-4" data-testid={`map-view-${ride.id}`}>
                      <MapComponent departure={ride.departure} destination={ride.destination}
                        departureCoords={getCityCoords(ride.departure) || { lat: ride.departure_lat, lng: ride.departure_lng }}
                        destinationCoords={getCityCoords(ride.destination) || { lat: ride.destination_lat, lng: ride.destination_lng }}
                        height="280px" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
